import { useEffect, useRef, useState } from "react";
import { usePlayer } from "@/stores/player";
import { Play, Pause, SkipBack, SkipForward, Music2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";

function fmt(s: number) {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export function PlayerBar() {
  const { current, url, isPlaying, setPlaying, next, prev } = usePlayer();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [time, setTime] = useState(0);
  const [dur, setDur] = useState(0);

  useEffect(() => {
    const a = audioRef.current;
    if (!a || !url) return;
    a.src = url;
    a.play().catch(() => setPlaying(false));
  }, [url, setPlaying]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    if (isPlaying) a.play().catch(() => {});
    else a.pause();
  }, [isPlaying]);

  if (!current) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-card/95 backdrop-blur-md shadow-[0_-4px_20px_-4px_oklch(0.21_0.03_250/0.08)]">
      <audio
        ref={audioRef}
        onTimeUpdate={(e) => setTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDur(e.currentTarget.duration)}
        onEnded={() => next()}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
      />
      <div className="mx-auto max-w-7xl px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:pb-3">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-3 min-w-0 flex-1 md:flex-none md:w-64">
            <div className="h-11 w-11 rounded-md bg-gradient-to-br from-primary/80 to-primary/40 flex items-center justify-center shrink-0">
              <Music2 className="h-5 w-5 text-primary-foreground" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{current.title}</div>
              <div className="text-xs text-muted-foreground truncate">
                {current.album?.title || current.album_title || "Sura"}
              </div>
            </div>
          </div>

          <div className="flex-1 hidden md:flex flex-col items-center gap-1">
            <div className="flex items-center gap-2">
              <button onClick={prev} className="p-2 hover:text-primary text-muted-foreground">
                <SkipBack className="h-4 w-4" />
              </button>
              <button
                onClick={() => setPlaying(!isPlaying)}
                className="h-9 w-9 rounded-full bg-foreground text-background flex items-center justify-center hover:scale-105 transition"
              >
                {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
              </button>
              <button onClick={next} className="p-2 hover:text-primary text-muted-foreground">
                <SkipForward className="h-4 w-4" />
              </button>
            </div>
            <div className="flex items-center gap-2 w-full max-w-md">
              <span className="text-[10px] text-muted-foreground tabular-nums w-9 text-right">{fmt(time)}</span>
              <Slider
                value={[time]}
                max={dur || 1}
                step={0.1}
                onValueChange={(v) => {
                  if (audioRef.current) audioRef.current.currentTime = v[0];
                }}
                className="flex-1"
              />
              <span className="text-[10px] text-muted-foreground tabular-nums w-9">{fmt(dur)}</span>
            </div>
          </div>

          <button
            onClick={() => setPlaying(!isPlaying)}
            className={cn(
              "md:hidden h-10 w-10 rounded-full bg-foreground text-background flex items-center justify-center",
            )}
          >
            {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
          </button>
        </div>
      </div>
    </div>
  );
}
